import { ManagedRelay, waitForManagedRelayClerkToken } from "@t3tools/client-runtime/relay";
import {
  createRuntimeCommand,
  isAtomCommandInterrupted,
} from "@t3tools/client-runtime/state/runtime";
import type { CloudEnvironmentControlResult } from "@t3tools/contracts";
import * as Cause from "effect/Cause";
import * as Effect from "effect/Effect";
import { useCallback } from "react";

import { connectionAtomRuntime } from "../connection/runtime";
import { useAtomCommand } from "./use-atom-command";

type CloudEnvironmentControlAction =
  | {
      readonly action: "provision-or-resume";
    }
  | {
      readonly action: "complete-connect-authorization";
      readonly authorizationCode: string;
    }
  | {
      readonly action: "stop";
    };

const cloudEnvironmentControlAction = createRuntimeCommand(connectionAtomRuntime, {
  label: "cloud-environment-control:action",
  concurrency: {
    mode: "singleFlight",
    key: () => "managed-cloud",
  },
  execute: (input: CloudEnvironmentControlAction, registry) =>
    Effect.gen(function* () {
      const clerkToken = yield* waitForManagedRelayClerkToken(registry);
      const relay = yield* ManagedRelay.ManagedRelayClient;
      if (input.action === "provision-or-resume") {
        return yield* relay.provisionOrResumeCloudEnvironment({ clerkToken });
      }
      if (input.action === "complete-connect-authorization") {
        return yield* relay.completeCloudEnvironmentConnectAuthorization({
          clerkToken,
          payload: { authorizationCode: input.authorizationCode },
        });
      }
      return yield* relay.stopCloudEnvironment({ clerkToken });
    }),
});

function actionFailureMessage(result: { readonly cause: Cause.Cause<unknown> }): string {
  const cause = Cause.squash(result.cause);
  return cause instanceof Error && cause.message.trim().length > 0
    ? cause.message
    : "Could not update the Fabrica Cloud environment.";
}

export interface CloudEnvironmentControlView {
  readonly available: boolean;
  readonly provisionOrResume: () => Promise<CloudEnvironmentControlResult>;
  readonly completeConnectAuthorization: (
    authorizationCode: string,
  ) => Promise<CloudEnvironmentControlResult>;
  readonly stopCloud: () => Promise<CloudEnvironmentControlResult>;
}

export function useCloudEnvironmentControl(): CloudEnvironmentControlView {
  const runAction = useAtomCommand(cloudEnvironmentControlAction, { reportFailure: false });
  const execute = useCallback(
    async (input: CloudEnvironmentControlAction): Promise<CloudEnvironmentControlResult> => {
      const result = await runAction(input);
      if (result._tag === "Failure") {
        if (isAtomCommandInterrupted(result)) {
          throw new Error("The Fabrica Cloud operation was interrupted.");
        }
        throw new Error(actionFailureMessage(result));
      }
      return result.value;
    },
    [runAction],
  );

  return {
    available: true,
    provisionOrResume: useCallback(() => execute({ action: "provision-or-resume" }), [execute]),
    completeConnectAuthorization: useCallback(
      (authorizationCode: string) =>
        execute({
          action: "complete-connect-authorization",
          authorizationCode,
        }),
      [execute],
    ),
    stopCloud: useCallback(() => execute({ action: "stop" }), [execute]),
  };
}
